import React from 'react';
import { DeckContext, ScaledSlide, Slide, fill } from '../../../components/decks/render';

// #region slideLabel
/** A slide's plain-text name for counters, thumbnails and screen readers. */
export function slideLabel(slide: Slide, values: Record<string, string>, i: number): string {
    const raw = typeof slide.fields.title === 'string' && slide.fields.title ? slide.fields.title : typeof slide.fields.quote === 'string' ? slide.fields.quote : '';
    const text = fill(raw, values).replace(/\*+/g, '').trim();
    return text || `Slide ${i + 1}`;
}

interface Props { slides: Slide[]; context: DeckContext; index: number; onPick: (i: number) => void }

// #region Thumb
const Thumb: React.FunctionComponent<{ slide: Slide; context: DeckContext; active: boolean; label: string; n: number; onClick: () => void; size: 'strip' | 'grid' }> = ({
    slide, context, active, label, n, onClick, size
}): React.ReactElement => (
    <button type="button" onClick={onClick} aria-label={`Go to slide ${n}: ${label}`} aria-current={active ? 'true' : undefined}
        className={`group relative block shrink-0 overflow-hidden rounded-md text-left outline-none transition focus-visible:ring-2 focus-visible:ring-[var(--wd-accent)] ${size === 'strip' ? 'w-28' : 'w-full'}
            ${active ? 'ring-2 ring-[var(--wd-accent)] ring-offset-2 ring-offset-[var(--wd-page)]' : 'opacity-80 ring-1 ring-black/10 hover:opacity-100'}`}>
        <ScaledSlide slide={slide} context={context} mode="view" />
        {size === 'grid' ? (
            <span className="flex items-baseline gap-2 bg-white px-3 py-2 text-[13px] text-[var(--wd-ink)]">
                <span className="tabular-nums text-[var(--wd-muted)]">{n}</span>
                <span className="truncate font-medium">{label}</span>
            </span>
        ) : null}
    </button>
);

// #region ThumbStrip
/** The desktop filmstrip under the slide. Keeps the current slide scrolled into view. */
export const ThumbStrip: React.FunctionComponent<Props> = ({ slides, context, index, onPick }): React.ReactElement => {
    const ref = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        const el = ref.current?.children[index] as HTMLElement | undefined;
        el?.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }, [index]);
    return (
        <div ref={ref} className="flex min-w-0 flex-1 gap-3 overflow-x-auto px-1 py-2 [scrollbar-width:thin]">
            {slides.map((s, i) => <Thumb key={s.id} slide={s} context={context} active={i === index} n={i + 1} label={slideLabel(s, context.values, i)} onClick={() => onPick(i)} size="strip" />)}
        </div>
    );
};

// #region OverviewGrid
/** Every slide at once; Esc or a pick closes it. */
export const OverviewGrid: React.FunctionComponent<Props & { onClose: () => void }> = ({ slides, context, index, onPick, onClose }): React.ReactElement => {
    const closeRef = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => { closeRef.current?.focus(); }, []);
    return (
        <div role="dialog" aria-modal="true" aria-label="All slides" className="fixed inset-0 z-50 overflow-y-auto backdrop-blur-sm" style={{ background: 'color-mix(in srgb, var(--wd-page) 96%, transparent)' }}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-black/5 bg-[var(--wd-page)] px-4 py-3 sm:px-8">
                <p className="text-sm font-semibold text-[var(--wd-ink)]">All slides <span className="font-normal text-[var(--wd-muted)]">· {slides.length}</span></p>
                <button ref={closeRef} type="button" onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold text-[var(--wd-ink)] hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--wd-accent)]">
                    Close
                </button>
            </div>
            <div className="mx-auto grid max-w-7xl grid-cols-2 gap-3 p-4 sm:grid-cols-3 sm:gap-6 sm:p-8 lg:grid-cols-4">
                {slides.map((s, i) => <Thumb key={s.id} slide={s} context={context} active={i === index} n={i + 1} label={slideLabel(s, context.values, i)} onClick={() => onPick(i)} size="grid" />)}
            </div>
        </div>
    );
};
